// ── Adapter Supabase do IWorkspaceWebhookRepository ───────────────
// CRUD da configuração de webhook por workspace + leitura do log de
// entregas. Ambas as tabelas são protegidas por RLS de owner da plataforma
// (Config/87), então este adapter só funciona no painel de Super Admin.

import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '@infra/supabase/client';
import type {
  IWorkspaceWebhookRepository,
  ListDeliveriesParams,
  WorkspaceWebhookConfig,
  WorkspaceWebhookConfigInput,
  WorkspaceWebhookEvent,
  WebhookDelivery,
} from '@modules/webhooks';
import { defaultWebhookEvents } from '@modules/webhooks';

// `workspace_webhooks` e `webhook_deliveries` ainda não estão em
// database.types.ts (regerar após aplicar Config/87). Até lá, cliente sem
// schema tipado — mesmo caminho já usado para `integration_events`.
const client = (): SupabaseClient => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase não inicializado.');
  return supabase as unknown as SupabaseClient;
};

interface ConfigRow {
  workspace_id: string;
  enabled: boolean | null;
  url: string | null;
  secret: string | null;
  events: Record<string, boolean> | null;
  custom_headers: Record<string, string> | null;
  updated_at: string | null;
}

interface DeliveryRow {
  id: string;
  workspace_id: string;
  event: string;
  dedupe_key: string | null;
  lead_id: string | null;
  url: string | null;
  status: string;
  http_status: number | null;
  attempts: number | null;
  error: string | null;
  duration_ms: number | null;
  created_at: string;
}

const toConfig = (row: ConfigRow): WorkspaceWebhookConfig => ({
  workspaceId: row.workspace_id,
  enabled: row.enabled === true,
  url: row.url || '',
  secret: row.secret || '',
  // Merge com o default: um evento novo lançado depois da última edição
  // entra ligado, em vez de ficar silenciosamente ausente.
  events: { ...defaultWebhookEvents(), ...(row.events || {}) } as Record<WorkspaceWebhookEvent, boolean>,
  customHeaders: row.custom_headers || {},
  updatedAt: row.updated_at,
});

const toDelivery = (row: DeliveryRow): WebhookDelivery => ({
  id: row.id,
  workspaceId: row.workspace_id,
  event: row.event,
  dedupeKey: row.dedupe_key,
  leadId: row.lead_id,
  url: row.url,
  status: row.status === 'success' ? 'success' : 'failed',
  httpStatus: row.http_status,
  attempts: row.attempts ?? 0,
  error: row.error,
  durationMs: row.duration_ms,
  createdAt: row.created_at,
});

const CONFIG_COLUMNS = 'workspace_id, enabled, url, secret, events, custom_headers, updated_at';

export class SupabaseWorkspaceWebhookRepository implements IWorkspaceWebhookRepository {
  getConfig = async (workspaceId: string): Promise<WorkspaceWebhookConfig | null> => {
    const { data, error } = await client()
      .from('workspace_webhooks')
      .select(CONFIG_COLUMNS)
      .eq('workspace_id', workspaceId)
      .maybeSingle();
    if (error) throw error;
    return data ? toConfig(data as ConfigRow) : null;
  };

  listConfigs = async (): Promise<WorkspaceWebhookConfig[]> => {
    const { data, error } = await client()
      .from('workspace_webhooks')
      .select(CONFIG_COLUMNS);
    if (error) throw error;
    return ((data || []) as ConfigRow[]).map(toConfig);
  };

  saveConfig = async (input: WorkspaceWebhookConfigInput): Promise<void> => {
    const { error } = await client()
      .from('workspace_webhooks')
      .upsert(
        {
          workspace_id: input.workspaceId,
          enabled: input.enabled,
          url: input.url || null,
          secret: input.secret || null,
          events: input.events,
          custom_headers: input.customHeaders || {},
        },
        { onConflict: 'workspace_id' },
      );
    if (error) throw error;
  };

  deleteConfig = async (workspaceId: string): Promise<void> => {
    const { error } = await client()
      .from('workspace_webhooks')
      .delete()
      .eq('workspace_id', workspaceId);
    if (error) throw error;
  };

  listDeliveries = async ({ workspaceId = null, limit = 50 }: ListDeliveriesParams): Promise<WebhookDelivery[]> => {
    let query = client()
      .from('webhook_deliveries')
      .select('id, workspace_id, event, dedupe_key, lead_id, url, status, http_status, attempts, error, duration_ms, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (workspaceId) query = query.eq('workspace_id', workspaceId);

    const { data, error } = await query;
    if (error) throw error;
    return ((data || []) as DeliveryRow[]).map(toDelivery);
  };
}
